/**
 * Unified token storage for SSO authentication across microfrontends
 * Uses sessionStorage with consistent keys for both cggenai-ui and cg-agentic-ui
 */

export const TOKEN_KEYS = {
  ACCESS: "pfz-cggenai-token-access",
  ID: "pfz-cggenai-token-id",
  REFRESH: "pfz-cggenai-token-refresh",
  EXPIRY: "pfz-cggenai-token-expiry",
  CODE_VERIFIER: "pfz-cggenai-token-codeverifier",
  USER_GIVEN_NAME: "pfz-cggenai-user-givenname",
  USER_ROLE: "pfz-cggenai-user-role",
  USER_PRIMARY_LANGUAGE: "pfz-cggenai-user-primary-language",
  USER_ENCODED_NTID: "pfz-cggenai-user-encoded-ntid",
} as const;

/**
 * Default buffer time for proactive token refresh (5 minutes)
 */
const DEFAULT_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

export interface TokenData {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
}

/**
 * Get all stored tokens
 */
export function getTokens(): TokenData {
  return {
    accessToken: sessionStorage.getItem(TOKEN_KEYS.ACCESS),
    refreshToken: sessionStorage.getItem(TOKEN_KEYS.REFRESH),
    expiresAt: sessionStorage.getItem(TOKEN_KEYS.EXPIRY)
      ? parseInt(sessionStorage.getItem(TOKEN_KEYS.EXPIRY)!, 10)
      : null,
  };
}

/**
 * Get access token
 */
export function getAccessToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEYS.ACCESS);
}

/**
 * Get ID token (used for Bearer authorization)
 */
export function getIdToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEYS.ID);
}

/**
 * Get refresh token
 */
export function getRefreshToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEYS.REFRESH);
}

/**
 * Check if the current access token is expired
 */
export function isTokenExpired(): boolean {
  const expiresAt = sessionStorage.getItem(TOKEN_KEYS.EXPIRY);
  if (!expiresAt) return true;
  return Date.now() > parseInt(expiresAt, 10);
}

/**
 * Check if token is near expiry (within buffer time)
 * @param bufferMs - Buffer time in milliseconds (default: 5 minutes)
 * @returns true if token will expire within buffer time or is already expired
 */
export function isTokenNearExpiry(
  bufferMs: number = DEFAULT_EXPIRY_BUFFER_MS,
): boolean {
  const expiresAt = sessionStorage.getItem(TOKEN_KEYS.EXPIRY);
  if (!expiresAt) return true;
  const expiryTime = parseInt(expiresAt, 10);
  return Date.now() >= expiryTime - bufferMs;
}

/**
 * Check if all required tokens exist in session storage
 */
export function hasValidTokens(): boolean {
  const accessToken = getAccessToken();
  const idToken = getIdToken();
  const refreshToken = getRefreshToken();
  const expiresAt = sessionStorage.getItem(TOKEN_KEYS.EXPIRY);
  return !!(accessToken && idToken && refreshToken && expiresAt);
}

/**
 * Check if user is authenticated (has valid tokens)
 */
export function isAuthenticated(): boolean {
  const accessToken = getAccessToken();
  return !!accessToken && !isTokenExpired();
}

/**
 * Store tokens in sessionStorage
 */
export function setTokens(
  accessToken: string,
  refreshToken: string,
  expiresAt: number,
): void {
  sessionStorage.setItem(TOKEN_KEYS.ACCESS, accessToken);
  sessionStorage.setItem(TOKEN_KEYS.REFRESH, refreshToken);
  sessionStorage.setItem(TOKEN_KEYS.EXPIRY, expiresAt.toString());
}

/**
 * Clear basic tokens (access, refresh, expiry)
 */
export function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_KEYS.ACCESS);
  sessionStorage.removeItem(TOKEN_KEYS.ID);
  sessionStorage.removeItem(TOKEN_KEYS.REFRESH);
  sessionStorage.removeItem(TOKEN_KEYS.EXPIRY);
}

/**
 * Clear all authentication tokens and user data from session storage
 */
export function clearAllTokens(): void {
  const tokenKeys = [
    TOKEN_KEYS.ACCESS,
    TOKEN_KEYS.ID,
    TOKEN_KEYS.REFRESH,
    TOKEN_KEYS.EXPIRY,
    TOKEN_KEYS.CODE_VERIFIER,
    TOKEN_KEYS.USER_GIVEN_NAME,
    TOKEN_KEYS.USER_ROLE,
    TOKEN_KEYS.USER_PRIMARY_LANGUAGE,
  ];
  tokenKeys.forEach((key) => sessionStorage.removeItem(key));
}

/**
 * Update only the access token and expiry (after refresh)
 */
export function updateAccessToken(
  accessToken: string,
  expiresAt: number,
): void {
  sessionStorage.setItem(TOKEN_KEYS.ACCESS, accessToken);
  sessionStorage.setItem(TOKEN_KEYS.EXPIRY, expiresAt.toString());
}

/**
 * Get encoded NTID from session storage (stored during authentication)
 */
export function getUserEncodedNtid(): string | null {
  return sessionStorage.getItem(TOKEN_KEYS.USER_ENCODED_NTID);
}

/**
 * Get user's given name from session storage
 */
export function getUserGivenName(): string | null {
  return sessionStorage.getItem(TOKEN_KEYS.USER_GIVEN_NAME);
}

/**
 * Get user's role from session storage
 */
export function getUserRole(): string | null {
  return sessionStorage.getItem(TOKEN_KEYS.USER_ROLE);
}
